import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { PaddiqLogo } from '@kangaroo/ui';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  decideMcpAuthorizationRequest,
  fetchMcpAuthorizationRequest,
  type McpAuthorizationRequest,
} from '../features/mcpConnections';
import {
  clearChatGptContinuation,
  connectionSessionRestriction,
  parseChatGptRequestId,
  storeChatGptContinuation,
} from '../lib/chatGptConnection';
import { useAuthStore } from '../store/authStore';

function requestError(error: unknown) {
  if (axios.isAxiosError(error) && error.response?.status === 404) {
    return 'This connection request is unavailable or has expired. Start a new connection from ChatGPT.';
  }
  return axios.isAxiosError(error) && typeof error.response?.data?.message === 'string'
    ? error.response.data.message
    : 'Could not complete the connection request. Please try again.';
}

export function ChatGptConnectPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const token = useAuthStore((state) => state.token);
  const user = useAuthStore((state) => state.user);
  const sessionKind = useAuthStore((state) => state.sessionKind);
  const supportSession = useAuthStore((state) => state.supportSession);
  const returnToKiosk = useAuthStore((state) => state.returnToKiosk);
  const restriction = connectionSessionRestriction({ sessionKind, supportSession, returnToKiosk });
  const requestId = parseChatGptRequestId(location.search);
  const [loadedRequest, setLoadedRequest] = useState<{
    data: McpAuthorizationRequest;
    token: string;
  } | null>(null);
  const request =
    loadedRequest?.token === token && loadedRequest.data.requestId === requestId
      ? loadedRequest.data
      : null;
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [retry, setRetry] = useState(0);
  const submitting = useRef(false);

  useEffect(() => {
    setLoadedRequest(null);
    setError(null);
    if (restriction) return;
    if (!requestId) {
      clearChatGptContinuation(window.sessionStorage);
      setError('This connection link is invalid. Start a new connection from ChatGPT.');
      return;
    }
    if (!token || !user) {
      storeChatGptContinuation(requestId, window.sessionStorage);
      navigate('/login', { replace: true });
      return;
    }
    clearChatGptContinuation(window.sessionStorage);
    let active = true;
    void fetchMcpAuthorizationRequest(requestId)
      .then((result) => {
        const scopes = result.scope.split(' ');
        if (
          result.requestId !== requestId ||
          !scopes.includes('paddiq:read') ||
          scopes.some(
            (s) =>
              ![
                'paddiq:read',
                'paddiq:write',
                'paddiq:destructive',
                'paddiq:operator:read',
                'paddiq:operator:campaigns',
              ].includes(s),
          )
        ) {
          throw new Error('Unexpected connection request');
        }
        if (active) setLoadedRequest({ data: result, token });
      })
      .catch((reason: unknown) => {
        if (active && !axios.isCancel(reason)) setError(requestError(reason));
      });
    return () => {
      active = false;
    };
  }, [navigate, requestId, restriction, retry, token, user?.id, user?.teamId]);

  async function decide(approved: boolean) {
    if (!request || !requestId || restriction || submitting.current) return;
    submitting.current = true;
    setIsSubmitting(true);
    setError(null);
    try {
      const result = await decideMcpAuthorizationRequest(requestId, approved);
      clearChatGptContinuation(window.sessionStorage);
      // The server validates the registered callback and builds both success and denial redirects.
      // Never navigate to a redirect URI supplied by this page's query string.
      window.location.assign(result.redirectUrl);
    } catch (reason) {
      if (!axios.isCancel(reason)) setError(requestError(reason));
      submitting.current = false;
      setIsSubmitting(false);
    }
  }

  return (
    <Box component="main" sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', p: 2 }}>
      <Paper variant="outlined" sx={{ width: '100%', maxWidth: 560, p: { xs: 3, sm: 4 } }}>
        <Stack spacing={2.5}>
          <PaddiqLogo variant="header" />
          <Typography component="h1" variant="h5" fontWeight={800}>
            {request ? `Connect ${request.clientName} to Paddiq` : 'Connect to Paddiq'}
          </Typography>
          {restriction ? <Alert severity="warning">{restriction}</Alert> : null}
          {error ? <Alert severity="error">{error}</Alert> : null}
          {!restriction && !error && !request ? (
            <CircularProgress aria-label="Loading connection request" />
          ) : null}
          {!restriction && request ? (
            <>
              <Box>
                <Typography fontWeight={700}>{request.account.name}</Typography>
                <Typography color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                  {request.account.email}
                </Typography>
                <Typography color="text.secondary">
                  Team: {request.teamName ?? (request.teamId ? 'Current team' : 'No team')}
                </Typography>
              </Box>
              <Divider />
              <Typography>{request.clientName} will be able to:</Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                <Typography component="li">Find race sessions you can access.</Typography>
                <Typography component="li">
                  Read session summaries, lap times, and lap comparisons.
                </Typography>
                {request.scope.split(' ').includes('paddiq:operator:read') ? (
                  <Typography component="li" color="error">
                    Read platform-wide operations data as an allowlisted administrator, across
                    customer workspaces.
                  </Typography>
                ) : null}
                {request.scope.split(' ').includes('paddiq:operator:campaigns') ? (
                  <Typography component="li" color="error">
                    Prepare campaigns and send confirmed batches to eligible recipients. Sending
                    email is irreversible.
                  </Typography>
                ) : null}
                {request.scope.split(' ').includes('paddiq:write') ? (
                  <Typography component="li">
                    Create and edit records you can manage, after preview and confirmation. Some
                    actions send invitations or start media processing.
                  </Typography>
                ) : null}
                {request.scope.split(' ').includes('paddiq:destructive') ? (
                  <Typography component="li" color="error">
                    Delete or release records and change account or team access you can administer,
                    after explicit confirmation. These actions may be irreversible.
                  </Typography>
                ) : null}
              </Box>
              <Typography variant="body2" color="text.secondary">
                {request.scope === 'paddiq:read'
                  ? 'This is read-only access for this account and team.'
                  : 'This connection can change data within your existing account and team permissions.'}{' '}
                You can disconnect the app at any time in Settings → Connections.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                After your decision, you will return to {request.redirectOrigin}.
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  onClick={() => void decide(true)}
                  disabled={isSubmitting}
                >
                  {isSubmitting
                    ? 'Returning to app…'
                    : request.scope === 'paddiq:read'
                      ? 'Allow read-only access'
                      : 'Allow requested access'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => void decide(false)}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
              </Stack>
            </>
          ) : (
            <Stack direction="row" spacing={1}>
              {error && requestId ? (
                <Button variant="outlined" onClick={() => setRetry((value) => value + 1)}>
                  Try again
                </Button>
              ) : null}
              {restriction || error ? (
                <Button
                  onClick={() =>
                    navigate(returnToKiosk || sessionKind === 'kiosk' ? '/kiosk' : '/drivers', {
                      replace: true,
                    })
                  }
                >
                  Return to Paddiq
                </Button>
              ) : null}
            </Stack>
          )}
        </Stack>
      </Paper>
    </Box>
  );
}
